<script lang="ts">
  import type { PictureMetadata, PictureMetadataTrackLoading } from '$lib/types';
  import GalleryImage from './GalleryImage.svelte';
  import Lightbox from './Lightbox.svelte';
  import NavArrow from './NavArrow.svelte';
  import HandleSwipe from '$lib/common/HandleSwipe.svelte';

  interface Props {
    imageModules: Record<string, PictureMetadata>;
  }

  let { imageModules }: Props = $props();

  const images = $derived<PictureMetadataTrackLoading[]>(
    Object.entries(imageModules)
      .sort(([picturePathA], [picturePathB]) => picturePathA.localeCompare(picturePathB))
      .map<PictureMetadataTrackLoading>(([path, module]) => {
        // 1. Get the last part of the path: "01-band-hyatt-1.jpg"
        const fullFilename = path.split('/').pop() ?? 'image';

        // 2. Split by the last dot to get the name and extension separately
        const lastDotIndex = fullFilename.lastIndexOf('.');
        const name = fullFilename.substring(0, lastDotIndex);
        const originalExtension = fullFilename.substring(lastDotIndex + 1);

        return {
          ...module,
          alt: name,
          extension: originalExtension,
          isLoading: true,
        };
      })
  );

  let currentIndex = $state(0);
  let itemsToShow = $state(3);
  let intervalId: NodeJS.Timeout | undefined = undefined;
  let selectedPhoto = $state<PictureMetadataTrackLoading | null>(null);

  const offset = $derived(currentIndex * (100 / itemsToShow));

  function getNextIndex(index: number, step: number): number {
    const next = index + step;
    if (next < 0) return Math.max(0, images.length - itemsToShow);
    if (next >= images.length) return 0;
    return next;
  }

  function checkWindowLoaded(index: number): boolean {
    const windowPhotos = images.slice(index, index + itemsToShow);

    if (windowPhotos.length < itemsToShow) {
      const remainingCount = itemsToShow - windowPhotos.length;
      windowPhotos.push(...images.slice(0, remainingCount));
    }

    return windowPhotos.every((photo) => !photo.isLoading);
  }

  function nextSlide(): void {
    const nextIndex = getNextIndex(currentIndex, itemsToShow);

    if (checkWindowLoaded(nextIndex)) {
      currentIndex = nextIndex;
    } else {
      console.log('Waiting for photos to load...');
    }
  }

  function manualPrev(): void {
    restartAutoCycleTimeout();
    currentIndex = getNextIndex(currentIndex, -itemsToShow);
  }

  function manualNext(): void {
    restartAutoCycleTimeout();
    currentIndex = getNextIndex(currentIndex, itemsToShow);
  }

  function goToImage(index: number) {
    restartAutoCycleTimeout();
    currentIndex = index * itemsToShow;
  }

  function stopAutoCycle(): void {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
  }

  function startAutoCycle(): void {
    if (intervalId) {
      return;
    }
    clearInterval(intervalId);
    intervalId = setInterval(nextSlide, 4000);
  }

  function restartAutoCycleTimeout(): void {
    stopAutoCycle();
    startAutoCycle();
  }

  function updateItemsToShow(): void {
    if (typeof window === 'undefined') return;
    const width = window.innerWidth;
    if (width < 640) itemsToShow = 1;
    else if (width < 1024) itemsToShow = 2;
    else itemsToShow = 3;
  }

  $effect(() => {
    updateItemsToShow();
    if (!selectedPhoto) startAutoCycle();
    return () => {
      stopAutoCycle();
    };
  });
</script>

<svelte:window onresize={updateItemsToShow} />

<HandleSwipe
  threshold={75}
  onSwipeLeft={manualPrev}
  onSwipeRight={manualNext}
  class="gallery-viewport"
>
  <NavArrow
    direction="PREV"
    onclick={manualPrev}
    ariaLabel="Previous photos"
    class="gallery-nav-arrow gallery-nav-prev"
  />
  <NavArrow
    direction="NEXT"
    onclick={manualNext}
    ariaLabel="Next photos"
    class="gallery-nav-arrow gallery-nav-next"
  />

  <div class="gallery-track" style:transform="translateX(-{offset}%)">
    {#each images as image, i (image.default.img.src)}
      <div class="gallery-slide" style:flex="0 0 {100 / itemsToShow}%">
        <button
          class="image-trigger"
          onclick={() => {
            stopAutoCycle();
            selectedPhoto = image;
          }}
          aria-label={image.alt}
        >
          <GalleryImage
            picture={image}
            alt={image.alt}
            priority={i < itemsToShow}
            onLoad={() => {
              image.isLoading = false;
            }}
            onError={() => {
              image.isLoading = false;
            }}
          />
        </button>
      </div>
    {/each}
  </div>
</HandleSwipe>

<div class="pagination-indicator">
  {#each Array(Math.ceil(images.length / itemsToShow)) as _, i (i + ' ' + itemsToShow)}
    <button
      class="dot"
      class:active={currentIndex === i * itemsToShow}
      onclick={() => goToImage(i)}
      aria-label="Go to slide {i + 1}"
    ></button>
  {/each}
</div>

<Lightbox
  picture={selectedPhoto}
  onClose={() => {
    selectedPhoto = null;
    startAutoCycle();
  }}
  onPrev={() => {
    const currentIdx = images.findIndex(
      (p) => p.default.img.src === selectedPhoto?.default.img.src
    );
    const prevIdx = (currentIdx - 1 + images.length) % images.length;
    selectedPhoto = images[prevIdx];
  }}
  onNext={() => {
    const currentIdx = images.findIndex(
      (p) => p.default.img.src === selectedPhoto?.default.img.src
    );
    const nextIdx = (currentIdx + 1) % images.length;
    selectedPhoto = images[nextIdx];
  }}
/>

<style>
  :global(.gallery-viewport) {
    width: 100%;
    overflow: hidden;
    position: relative;
  }

  /* Base Arrow Style: Visible by default for touch/tablets */
  :global(.gallery-nav-arrow) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    opacity: 1;
    transition:
      opacity 0.2s ease,
      background-color 0.2s ease;
  }

  /* Desktop Only: Hide until hover */
  @media (hover: hover) {
    :global(.gallery-nav-arrow) {
      opacity: 0;
    }

    :global(.gallery-viewport:hover) :global(.gallery-nav-arrow) {
      opacity: 1;
    }
  }

  /* Mobile Only: Hide arrows to keep it clean */
  @media (max-width: 767px) {
    :global(.gallery-nav-arrow) {
      display: none;
    }
  }

  :global(.gallery-nav-arrow:hover) {
    background: rgba(0, 0, 0, 0.8);
  }

  :global(.gallery-nav-prev) {
    left: 1rem;
  }

  :global(.gallery-nav-next) {
    right: 1rem;
  }

  .gallery-track {
    display: flex;
    transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .gallery-slide {
    padding: 0 10px;
    box-sizing: border-box;
  }

  .image-trigger {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    width: 100%;
    display: block;
  }

  /* Pagination Styling */
  .pagination-indicator {
    display: flex;
    justify-content: center;
    gap: 8px; /* Mobile Gap */
    margin-top: 1rem;
  }

  .dot {
    width: 8px; /* Mobile Size */
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    border: none;
    padding: 0;
    cursor: pointer;
    transition:
      background-color 0.3s,
      transform 0.3s;
  }

  /* Tablet: 2x Size and Spread  */
  @media (min-width: 768px) {
    .pagination-indicator {
      gap: 14px; /* 2x Spread */
    }
    .dot {
      width: 14px; /* 2x Size */
      height: 14px;
    }
  }

  /* Desktop: 1.5x Size and Spread  */
  @media (min-width: 1024px) {
    .pagination-indicator {
      gap: 10px; /* 1.5x Spread */
    }
    .dot {
      width: 10px; /* 1.5x Size */
      height: 10px;
    }
  }

  .dot.active {
    background: var(--light-purple, #fff);
    transform: scale(1.2);
  }

  /* Keep your existing rule to hide dots on ultra-wide desktops if preferred [cite: 45, 77] */
  @media (min-width: 1280px) {
    .pagination-indicator {
      display: none;
    }
  }
</style>
